import React, { useState, useEffect, useContext } from "react";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Link, useNavigate, useLocation } from "react-router-dom";
import classnames from "classnames";
import PropTypes from "prop-types";
import { serverHost } from "variables/serverdata";
import { useKeycloak } from "variables/keycloak";
import { ThemeContext } from "../../contexts/ThemeContext";
import SiteSelector from "./SiteSelector";
import {
  Button,
  Collapse,
  DropdownMenu,
  DropdownItem,
  UncontrolledDropdown,
  DropdownToggle,
  Form,
  Media,
  Navbar,
  NavItem,
  Nav,
  Container,
} from "reactstrap";

function AdminNavbar({ theme, sidenavOpen, toggleSidenav }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [userName, setUserName] = useState("");
  const [userAvatar, setUserAvatar] = useState("");
  const [clients, setClients] = useState([]);
  const [selectedSite, setSelectedSite] = useState(null);
  const [sites, setSites] = useState({});
  const [userRole, setUserRole] = useState("");
  const { theme: currentTheme, toggleTheme, themePreference } =
    useContext(ThemeContext);
  const keycloak = useKeycloak();
  const [clientId, setClientId] = useState("");

  const pluralizeEntity = (entity) => {
    const irregulars = {
      //directory: "directories",
    };

    if (irregulars[entity]) return irregulars[entity];
    if (entity.endsWith("y")) return `${entity.slice(0, -1)}ies`;
    return `${entity}s`;
  };

  const getTableEntityFromFormEntity = (entity) => {
    const routeMap = {
      v_gateways: "gateways",
      v_dialplans: "dialplan-routes",
    };

    return routeMap[entity] || pluralizeEntity(entity);
  };

  const getTableRouteFromFormRoute = (pathname) => {
    const parts = pathname.split("/").filter(Boolean);

    if (parts.length < 4) return null;

    const [admin, moduleName, action, entity] = parts;

    if (admin !== "admin") return null;
    if (action !== "add" && action !== "edit") return null;

    const tableEntity = getTableEntityFromFormEntity(entity);

    return `/${admin}/${moduleName}/${tableEntity}`;
  };

  const fetchSites = async (clientUuid) => {
    try {
      console.log("AdminNavbar - fetchSites via backend, client:", clientUuid);

      const token =
        JSON.parse(localStorage.getItem("user") || "{}")?.access_token ||
        localStorage.getItem("token");

      const resp = await fetch(`${serverHost}/fetch_client_sites`, {
        method: "GET",
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          client: clientUuid,
        },
      });

      const json = await resp.json();
      if (!resp.ok) {
        console.error("AdminNavbar - fetchSites failed:", json);
        return [];
      }

      const rows = json.data || [];
      // normalize to what the UI expects
      return rows.map((s) => ({
        ...s,
        site_name: s.site_name || s.company_name,
      }));
    } catch (e) {
      console.error("AdminNavbar - fetchSites error:", e);
      return [];
    }
  };

  const fetchClients = async () => {
    try {
      const currentUser = JSON.parse(localStorage.getItem("user"));
      if (!currentUser) return;

      // ADMIN / MGNT / USER → fetch their assigned site directly
      if (["ADMIN", "MGNT", "USER"].includes(currentUser.user_role)) {
        const token =
          currentUser?.access_token || localStorage.getItem("token") || "";

        const res = await fetch(`${serverHost}/fetch_user_site`, {
          method: "GET",
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });

        const json = await res.json();
        if (!res.ok) {
          console.error("Failed to fetch user site:", json);
          return;
        }

        const siteData = json.data;
        const fakeClient = {
          client_uuid: siteData.client_uuid || "single-client",
          company_name: siteData.company_name || siteData.site_name || "Client",
          country: siteData.country || null,
        };

        setClients([fakeClient]);
        setSites({ [fakeClient.client_uuid]: [siteData], global: [siteData] });
        setSelectedSite(siteData);
        localStorage.setItem("selectedSite", JSON.stringify(siteData));
        window.dispatchEvent(new CustomEvent("siteChanged", { detail: siteData }));

        // also patch the user object in localStorage
        const currentStoredUser = JSON.parse(localStorage.getItem("user") || "{}");
        const patchedUser = {
          ...currentStoredUser,
          client_uuid: siteData.client_uuid || currentStoredUser.client_uuid,
          site_uuid: siteData.site_uuid || currentStoredUser.site_uuid,
          site: siteData,
        };

        localStorage.setItem("user", JSON.stringify(patchedUser));

        return;
      }

    } catch (error) {
      console.error("fetchClients error:", error);
    }
  };

  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    if (!storedUser) return;

    const userData = JSON.parse(storedUser);
    const storedSite = JSON.parse(localStorage.getItem("selectedSite"));

    console.log("AdminNavbar - Complete user data from localStorage:", userData);

    themePreference(userData.theme);

    const resolvedClientUuid =
      userData.client_uuid || userData.client?.client_uuid || null;

    const patchedUser = { ...userData, client_uuid: resolvedClientUuid };
    localStorage.setItem("user", JSON.stringify(patchedUser));

    setClientId(resolvedClientUuid || "");
    setUserName(userData.first_name || "");
    setUserAvatar(userData.avatar || "");
    setUserRole(userData.user_role || "");
    setSelectedSite(storedSite || null);

    // ADMIN / MGNT / USER
    if (["ADMIN", "MGNT", "USER"].includes(userData.user_role)) {
      fetchClients();
    }

    // GLOBAL / SALES
    if (
      (userData.user_role === "GLOBAL" || userData.user_role === "SALES") &&
      userData.dealer_id
    ) {
      fetchClients(); //fetchClients() currently doesn’t use dealer_id
    }

    if (userData.user_role === "SUPERADMIN") {
      const clientUuid = resolvedClientUuid;
      if (!clientUuid) {
        console.warn("SUPERADMIN has no client_uuid");
      } else {
        const clientObj =
          userData.client || { client_uuid: clientUuid, company_name: "Client" };

        setClients([clientObj]);

        fetchSites(clientUuid).then((sitesArr) => {
          setSites({ [clientUuid]: sitesArr, global: sitesArr });
          localStorage.setItem("clientSites", JSON.stringify(sitesArr));

          if (sitesArr.length > 0) {
            const stored = JSON.parse(localStorage.getItem("selectedSite"));

            const initial =
              stored && sitesArr.some((s) => s.site_uuid === stored.site_uuid)
                ? stored
                : sitesArr[0];
            setSelectedSite(initial);
            localStorage.setItem("selectedSite", JSON.stringify(initial));

            window.dispatchEvent(new CustomEvent("siteChanged", { detail: initial }));
          }
        });
      }
    }

    // GLOBAL/SALES only: restore cached global sites (merge, don’t overwrite)
    if (userData.user_role === "GLOBAL" || userData.user_role === "SUPERADMIN" || userData.user_role === "SALES") {
      const storedClientSites = JSON.parse(localStorage.getItem("clientSites"));
      if (storedClientSites && Array.isArray(storedClientSites)) {
        setSites((prev) => ({ ...prev, global: storedClientSites }));
      }
    }

    const handleSiteChange = (event) => {
      const foundSite = event.detail;

      if (!foundSite) {
        console.warn("AdminNavbar - siteChanged event fired with no site");
        return;
      }

      setSelectedSite(foundSite);
      toast.success(`Switched to site: ${foundSite.site_name || foundSite.company_name}`);

      try {
        const user = JSON.parse(localStorage.getItem("user") || "{}");
        user.site_uuid = foundSite.site_uuid;
        user.site = foundSite;
        user.client_uuid = foundSite.client_uuid;

        localStorage.setItem("user", JSON.stringify(user));
        localStorage.setItem("selectedSite", JSON.stringify(foundSite));
        setClientId(foundSite.client_uuid);
      } catch (e) {
        console.warn("AdminNavbar - failed updating user/site in localStorage", e);
      }

      window.dispatchEvent(new Event("appContextChanged"));
    };

    window.addEventListener("siteChanged", handleSiteChange);
    return () => window.removeEventListener("siteChanged", handleSiteChange);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSiteSelect = (site) => {
    if (!site) return;

    const redirectRoute = getTableRouteFromFormRoute(location.pathname);

    setSelectedSite(site);

    try {
      const currentUserRaw = localStorage.getItem("user");
      if (currentUserRaw) {
        const currentUser = JSON.parse(currentUserRaw);
        currentUser.site_uuid = site.site_uuid;
        currentUser.site = site;
        currentUser.client_uuid = site.client_uuid;
        localStorage.setItem("user", JSON.stringify(currentUser));
        setClientId(site.client_uuid);
      }
    } catch (e) {
      console.warn("AdminNavbar - failed updating user in localStorage", e);
    }

    localStorage.setItem("selectedSite", JSON.stringify(site));

    if (
      location.pathname !== "/dealer" &&
      location.pathname !== "/dealer/dashboard"
    ) {
      window.dispatchEvent(new CustomEvent("siteChanged", { detail: site }));
    }

    if (redirectRoute) {
      navigate(redirectRoute, { replace: true });
    }
  };

  const handleThemeChange = async () => {
    const currentUser = JSON.parse(localStorage.getItem("user"));
    const nextTheme = currentTheme === "dark" ? "light" : "dark";
    toggleTheme();

    const resp = await fetch(`${serverHost}/theme/${currentUser.user_uuid}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ theme: nextTheme }),
    });

    const updatedUser = { ...currentUser, theme: nextTheme };
    localStorage.setItem("user", JSON.stringify(updatedUser));
    if (!resp.ok) throw new Error("Failed to update theme for the user");
  };

  const handleLogout = () => {
    localStorage.removeItem("user");
    localStorage.removeItem("selectedSite");
    localStorage.removeItem("clientSites");
    localStorage.removeItem("clientId");
    try {
      keycloak?.logout({
        redirectUri: `${window.location.origin}/auth/login`,
      });
    } catch (error) {
      console.error("Error during logout:", error);
    }
  };

  return (
    <>
      <style>
        {`
          .client-item { position: relative; }
          .client-name { color: ${currentTheme === "light" ? "#8898aa" : "#cbd5e0"
          }; font-size: 0.875rem; padding: 0.5rem 1rem; font-weight: 500; }
          .sites-menu { display: none; position: absolute; right: 100%; top: 0; min-width: 200px; background: ${currentTheme === "light" ? "white" : "#1f1f1f"
          }; border: 1px solid rgba(0,0,0,.15); border-radius: 0.375rem; box-shadow: 0 0.5rem 1rem rgba(0,0,0,.175); z-index: 1000; color: ${theme === "light" ? "#212529" : "#f8f9fa"
          }; }
          .client-item:hover .sites-menu { display: block; }
        `}
      </style>

      <Navbar
        className={classnames(
          "navbar-top navbar-expand border-bottom",
          { "navbar-dark bg-default": currentTheme === "dark" },
          { "navbar-light bg-secondary": currentTheme === "light" }
        )}
      >
        <Container fluid>
          <Collapse navbar isOpen={true}>
            <Form
              className={classnames(
                "navbar-search form-inline mr-sm-3",
                { "navbar-search-light": currentTheme === "dark" },
                { "navbar-search-dark": currentTheme === "light" }
              )}
            >
              <>
                {(userRole === "GLOBAL" || userRole === "SALES") &&
                  location.pathname !== "/dealer" &&
                  location.pathname !== "/dealer/dashboard" && (
                    <Button
                      color="primary"
                      className="mr-3"
                      onClick={() => {
                        localStorage.removeItem("selectedSite");
                        localStorage.removeItem("clientSites");
                        setSelectedSite(null);
                        window.location.href = "/dealer";
                      }}
                    >
                      Dealer Dashboard
                    </Button>
                  )}
              </>

              {(userRole === "GLOBAL" || userRole === "SALES") &&
                clients &&
                clients.length > 0 && (
                  <h2 className="text-black d-inline-block mb-0">
                    {selectedSite?.site_name || selectedSite?.company_name || ""}
                  </h2>
                )}
            </Form>

            <Nav className="align-items-center ml-md-auto" navbar>
              <NavItem className="d-xl-none">
                <div
                  className={classnames(
                    "pr-3 sidenav-toggler",
                    { active: sidenavOpen },
                    { "sidenav-toggler-dark": currentTheme === "dark" }
                  )}
                  onClick={toggleSidenav}
                >
                  <div className="sidenav-toggler-inner">
                    <i className="sidenav-toggler-line" />
                    <i className="sidenav-toggler-line" />
                    <i className="sidenav-toggler-line" />
                  </div>
                </div>
              </NavItem>

              {!(
                location.pathname === "/dealer" ||
                location.pathname === "/dealer/dashboard"
              ) && (
                  <SiteSelector
                    userRole={
                      userRole === "SUPERADMIN" || userRole === "SALES"
                        ? "GLOBAL"
                        : userRole
                    }
                    location={location}
                    selectedSite={selectedSite}
                    sites={sites}
                    clients={clients}
                    handleSiteSelect={handleSiteSelect}
                  />
                )}

              <NavItem className="d-flex align-items-center theme-button">
                <Button
                  color={currentTheme === "light" ? "dark" : "light"}
                  size="sm"
                  onClick={handleThemeChange}
                  className="ml-2 theme-toggle"
                >
                  {currentTheme === "light" ? (
                    <i className="fas fa-moon fa-lg" />
                  ) : (
                    <i className="fas fa-sun fa-lg" style={{ color: "#FFAA33" }} />
                  )}
                </Button>
              </NavItem>
            </Nav>

            <Nav className="align-items-center ml-auto ml-md-0 user-dropdown" navbar>
              <UncontrolledDropdown nav>
                <DropdownToggle
                  className="nav-link pr-0"
                  color=""
                  tag="a"
                  style={{ cursor: "pointer" }}
                >
                  <Media className="align-items-center">
                    <span className="avatar avatar-sm rounded-circle">
                      <img
                        alt="User Avatar"
                        style={{ height: "40px", width: "40px" }}
                        src={
                          userAvatar &&
                            userAvatar !== "null" &&
                            userAvatar.trim()
                            ? `${serverHost}/avatars/${userAvatar}?t=${Date.now()}`
                            : require("assets/img/theme/avatar/vue.jpg")
                        }
                      />
                    </span>
                    <Media className="ml-2 d-none d-lg-block">
                      <span
                        className="mb-0 text-sm font-weight-bold"
                        style={{ color: "white" }}
                      >
                        {userName} <i className="fas fa-chevron-down ml-2" />
                      </span>
                    </Media>
                  </Media>
                </DropdownToggle>

                <DropdownMenu right className="user-actions">
                  <DropdownItem className="noti-title" header tag="div">
                    <h6 className="text-overflow m-0">Profile Actions</h6>
                  </DropdownItem>

                  <DropdownItem tag={Link} to="/admin/profile">
                    <i className="fas fa-user" />
                    <span style={{ marginLeft: "6px" }}>My Account</span>
                  </DropdownItem>

                  {userRole === "USER" && clientId === "" && (
                    <DropdownItem tag={Link} to="/admin/home">
                      <i className="fas fa-users" />
                      <span>Join Company</span>
                    </DropdownItem>
                  )}

                  {(userRole === "GLOBAL" ||
                    userRole === "SUPERADMIN" ||
                    userRole === "SALES") && (
                      <DropdownItem tag={Link} to="/admin/companyprofile">
                        <i className="fas fa-sitemap" />
                        <span>My Company Profile</span>
                      </DropdownItem>
                    )}

                  {(userRole === "GLOBAL" ||
                    userRole === "SUPERADMIN" ||
                    userRole === "ADMIN" ||
                    userRole === "MGNT" ||
                    userRole === "SALES") && (
                      <DropdownItem tag={Link} to="/admin/companyusers">
                        <i className="fas fa-users" />
                        <span>My Managed Users</span>
                      </DropdownItem>
                    )}

                  {(userRole === "GLOBAL" || userRole === "SALES") && (
                    <DropdownItem tag={Link} to="/admin/companyclients">
                      <i className="fas fa-building" />
                      <span style={{ marginLeft: "6px" }}>Clients</span>
                    </DropdownItem>
                  )}

                  {(userRole === "GLOBAL" ||
                    userRole === "SUPERADMIN" ||
                    userRole === "SALES") && (
                      <DropdownItem tag={Link} to="/admin/companysites">
                        <i className="fas fa-map-marker-alt" />
                        <span style={{ marginLeft: "6px" }}>Client Sites</span>
                      </DropdownItem>
                    )}

                  <DropdownItem divider />

                  <DropdownItem
                    tag={Link}
                    to="#pablo"
                    onClick={handleLogout}
                    style={{ cursor: "pointer" }}
                  >
                    <i className="fas fa-sign-out-alt" />
                    <span style={{ marginLeft: "6px" }}>Logout</span>
                  </DropdownItem>
                </DropdownMenu>
              </UncontrolledDropdown>
            </Nav>
          </Collapse>
        </Container>
      </Navbar>
    </>
  );
}

AdminNavbar.defaultProps = {
  toggleSidenav: () => { },
  sidenavOpen: false,
  currentTheme: "dark",
};

AdminNavbar.propTypes = {
  toggleSidenav: PropTypes.func,
  sidenavOpen: PropTypes.bool,
  currentTheme: PropTypes.oneOf(["dark", "light"]),
};

export default AdminNavbar;